import React, { CSSProperties } from 'react';

import { DEFAULT_STATE, TableStates } from '../../../typings/table';
import { slugify } from '../../../utils/slugify';
import { makeClassNames } from '../../../utils/styles';
import Cta from '../../atoms/cta/cta';

/**
 * IColumn interface
 * Each COLUMN has PROPS to RENDER both DATA and CSS class names
 * @param title - The NAME to show in TH element
 * @param colspan - The TH COLSPAN (use 0 to hide a column)
 * @param data - The NAME of the PROP in ROW array of objects containing DATA to RENDER
 * @param classModifier - NAME to APPEND to CLASS NAME for this COLUMN (STATIC, e.g. used for ALL ROWS) using BEM, see http://getbem.com/introduction/
 * @param classColumn - Append ROW DATA to CLASS NAME for this COLUMN (DYNAMIC, e.g. can change for each ROW) using BEM
 * @param render - Render prop (function to invoke when rendering this column cell, passing ROW DATA as PROPS)
 */
export interface IColumn {
  classColumn?: string;
  classModifier?: string;
  colspan?: number;
  data?: string;
  render?: Function;
  title?: string;
}

/**
 * IRow interface
 * Components which uses TABLE must EXTEND this interface
 * @param classRow - This function will be called passing ROW DATA and ROW INDEX as params; expects TRUE/FALSE in return
 * @param rowLink - The LINK of the entire row - its should be useful in most cases for use the full row as single clickable element
 */
export interface IRow {
  classRow?: Function;
  rowLink?: string;
  trackingData?: object;
}

export interface ITableData<C, R> {
  className?: string; // ROOT NAME delle CLASSI CSS
  columns: C[]; // Array<IColumn>
  rows: R[]; // Array<IRow>
  showTitles?: boolean; // Visibilità del THEAD
  state?: TableStates; // to hide fastest lap column or other states
  hasLgpAnalytics?: boolean;
}

const classNames = (className: string, showTitles: boolean) => {
  return makeClassNames({
    table: className,
    head: [`${className}__head`, !showTitles && `${className}__head--hidden`],
    headRow: `${className}__head-row`,
    headCell: `${className}__head-cell`,
    body: `${className}__body`,
    bodyRow: `${className}__body-row`,
    bodyRowSelected: `${className}__body-row--selected`,
    bodyCell: `${className}__body-cell`,
    bodyCellSpan: `${className}__body-cell-span`,
    link: `${className}__link`,
    linkTh: `${className}__link-th`,
  });
};

// GENERIC TABLE component has no CSS, so I use STYLE to HIDE TH when needed
const cssHide: CSSProperties = {
  display: 'none',
};

/**
 * Renders a TABLE passing an ARRAY of COLUMNS and an ARRAY of ROWS
 * Each COLUMN specify which DATA to RENDER from each ROW
 * @param columns - An ARRAY of OBJECTS, see interface IColumn
 * @param rows - An ARRAY of OBJECTS
 * @param className - The name to use for generating CSS class names
 * @param showTitles - CSS visibility of THEAD
 */
const Table = <C extends IColumn, R extends IRow>({
  /* istanbul ignore next: default value */ className = 'table',
  /* istanbul ignore next: default value */ showTitles = true,
  columns,
  rows,
  hasLgpAnalytics,
  /* istanbul ignore next: default value */ state = DEFAULT_STATE,
}: ITableData<C, R>) => {
  const cn = classNames(className, showTitles);
  const hasLinks = rows.find((row) => Boolean(row.rowLink));
  return (
    <table
      className={cn.table}
      data-state={state}
      data-analytics-element={hasLgpAnalytics ? 'sportsdata-risultati' : null}
    >
      <thead className={cn.head}>
        <tr className={cn.headRow}>
          {hasLinks && <th className={cn.linkTh} />}
          {columns.map((column: C, key: number) => {
            const colspan = column.hasOwnProperty('colspan') ? column.colspan : 1;
            const classModifier = column.hasOwnProperty('classModifier')
              ? `${cn.headCell} ${cn.headCell}--${column.classModifier}`
              : cn.headCell;
            // FIXME: COLSPAN=0 => HIDE TH, don't skip it (for SEO purposes: same number of COLUMNS in HEAD and BODY)
            return colspan === 0 ? (
              <th key={slugify(column.title ?? `column-${key}`)} style={cssHide} />
            ) : (
              <th key={slugify(column.title ?? `column-${key}`)} colSpan={colspan} className={classModifier}>
                {column.title}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody className={cn.body}>
        {rows &&
          rows.map((row: R, index: number) => {
            const classRow = typeof row.classRow === 'function' && row.classRow(row, index) ? cn.bodyRowSelected : '';
            const rowHasLink = Boolean(row.rowLink);
            return (
              // FIXME: key={index} -> ROW has no UNIQUE ID in IRow interface!
              <tr key={index} className={`${cn.bodyRow} ${classRow}`}>
                {columns.map((column: C, key: number) => {
                  const classModifier: string = column.hasOwnProperty('classModifier')
                    ? `${cn.bodyCell}--${column.classModifier}`
                    : '';
                  const classColumn =
                    column.hasOwnProperty('classColumn') && row.hasOwnProperty(column.classColumn!)
                      ? // @ts-ignore
                        `${cn.bodyCell}--${row[column.classColumn!]}`
                      : '';
                  // @ts-ignore
                  const data = column.data && row.hasOwnProperty(column.data) ? row[column.data] : ' ';

                  return (
                    <td key={key} className={`${cn.bodyCell} ${classModifier} ${classColumn}`}>
                      <span className={cn.bodyCellSpan}>
                        {typeof column.render === 'function' ? column.render(data) : data}
                      </span>
                    </td>
                  );
                })}
                {rowHasLink && (
                  <td className={cn.link}>
                    <Cta href={row.rowLink} uppercase={false} trackingData={row.trackingData} hasWrapper={false} />
                  </td>
                )}
              </tr>
            );
          })}
      </tbody>
    </table>
  );
};

export default Table;
